import { Injectable, PLATFORM_ID, makeStateKey, StateKey, TransferState, inject } from '@angular/core';
import { PrediggoService } from '@app/core/prediggo/services/prediggo.service';
import { PrediggoSimplePageContent } from '@app/core/prediggo/model/prediggo-simple-page-content.model';
import { PrediggoPageEnum } from '@app/core/prediggo/model/prediggo-page.enum';
import { PrediggoParameters } from '@app/core/prediggo/model/prediggo-parameters.model';
import { BehaviorSubject, Observable, AsyncSubject, finalize } from 'rxjs';
import { PrediggoSlot } from '@app/core/prediggo/model/prediggo-slot.model';

import { isPlatformServer, isPlatformBrowser, Location } from '@angular/common';
import { PrediggoSimplePageContentResponse } from '@app/core/prediggo/model/prediggo-simple-page-content-response.model';
import { PrediggoRecoBlock } from '@app/core/prediggo/model/prediggo-reco-block.model';
import { Router } from '@angular/router';
import { TagManagerService } from '@app/core/tag-manager/services/tag-manager.service';
import { SomRecipePage } from '@app/core/models/recipe-page.model';
import { prediggoTranslation } from '@assets/prediggoObject';
import { PAGE_TYPE } from '../enums/page-type.enum';
import { TranslateService } from '@ngx-translate/core';
import { SomProductPage } from '@app/core/models/product-page.model';
import { PrediggoProductAdapter } from '@app/core/prediggo/adapters/product-adapter';
import { PrediggoRecipeAdapter } from '@app/core/prediggo/adapters/recipe-adapter';
import { PrediggoFacetGroup } from '@app/core/prediggo/model/prediggo-facet-group.model';
import { PrediggoSortingOption } from '@app/core/prediggo/model/prediggo-sorting-option.model';
import { PrediggoArticleAdapter } from '@app/core/prediggo/adapters/article-adapter';
import { isValidGtin } from '@app/business/rules';

const STATEKEY_RECIPE_LIST_RECO: StateKey<any> = makeStateKey<any>('recipeListReco');
const charsMapConvert = require('@app/core/charsMap');

export type ResultTotal = {
  recipe: number
  product: number
  article: number
}

@Injectable({
  providedIn: 'root'
})
export class SearchMainService {
  private router = inject(Router);
  private location = inject(Location);
  private prediggo = inject(PrediggoService);
  private translate = inject(TranslateService);
  private transferState = inject(TransferState);
  private platformId = inject(PLATFORM_ID);
  private tagManager = inject(TagManagerService);


  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly NUMBER_OF_ITEMS_PER_LOADING = 21;

  private somQuery: PrediggoSimplePageContent;

  public total: ResultTotal = {
    recipe: 0,
    product: 0,
    article: 0
  };
  public productsPage: {results: SomProductPage[], facets: PrediggoFacetGroup[], sortings: PrediggoSortingOption[], totalResults: number} = {
    results: [],
    facets: [],
    sortings: [],
    totalResults: 0
  }
  public recipesPage: {results: SomRecipePage[], facets: PrediggoFacetGroup[], sortings: PrediggoSortingOption[], totalResults: number} = {
    results: [],
    facets: [],
    sortings: [],
    totalResults: 0
  }
  public articlesPage: {results: any[], facets: PrediggoFacetGroup[], sortings: PrediggoSortingOption[], totalResults: number} = {
    results: [],
    facets: [],
    sortings: [],
    totalResults: 0
  }

  public isRequestDone: boolean = false;
  public isResults: boolean = false;

  private windowReference: any;

  private recipeListSubject: BehaviorSubject<SomRecipePage[]> = new BehaviorSubject([]);

  public get recipeList(): Observable<SomRecipePage[]> {
    return this.recipeListSubject.asObservable();
  }

  public get currentRecipeList(): SomRecipePage[] {
    return this.recipeListSubject.getValue();
  }

  public querySearch: BehaviorSubject<string> = new BehaviorSubject('');

  public querySearchObs: Observable<string> = this.querySearch.asObservable();

  /**
   * keywords
   *
   * @memberof SearchMainService
   */
  public set keywords(value: string) {
    this.somQuery.parameters.query = value || '';
  }
  public get keywords(): string {
    return this.somQuery.parameters.query || '';
  }
  public get urlKeywords(): string {
    return charsMapConvert(this.somQuery.parameters.query);
  }
  public set pageType(value: PAGE_TYPE) {
    switch (value) {
      case PAGE_TYPE.PRODUCTS:
        this.somQuery.pageId = PrediggoPageEnum.recherche_nos_produits;
        break;
      case PAGE_TYPE.RECIPES:
        this.somQuery.pageId = PrediggoPageEnum.recherche_nos_recettes;
        break;
      case PAGE_TYPE.ARTICLES:
        this.somQuery.pageId = PrediggoPageEnum.recherche_nos_articles;
        break;
      default:
        this.somQuery.pageId = PrediggoPageEnum.pre_produit_recette;
    }
  }
  public get pageType(): PAGE_TYPE {
    switch (this.somQuery.pageId) {
      case PrediggoPageEnum.recherche_nos_articles:
        return PAGE_TYPE.ARTICLES;
      case PrediggoPageEnum.recherche_nos_produits:
        return PAGE_TYPE.PRODUCTS;
      case PrediggoPageEnum.recherche_nos_recettes:
        return PAGE_TYPE.RECIPES;
      default:
        return PAGE_TYPE.ALL;
    }
  }
  public set sortings(value: string) {
    this.somQuery.parameters.sortingCode = value || '';
  }

  public get sorting(): string {
    return this.somQuery?.parameters?.sortingCode || ''
  }

  public set filters(values: Record<string, string[]>) {
    this.somQuery.parameters.filters = values;
  }
  public get filters(): Record<string, string[]> {
    return this.somQuery.parameters.filters as Record<string, string[]>;
  }
  public isEmpty(): boolean {
    const filters = this.filters;
    for (const key in filters) {
        if (Object.prototype.hasOwnProperty.call(filters, key)) {
            return false;
        }
    }
    return true;
  }

  public set pageNumber(value: number) {
    this.somQuery.parameters.page = value;
  }
  public get pageNumber(): number {
    return this.somQuery?.parameters?.page || 1;
  }

  public constructor() {
    this.somQuery = PrediggoSimplePageContent.create();
    this.somQuery.parameters = PrediggoParameters.create();
    this.somQuery.parameters.resultsPerPage = this.NUMBER_OF_ITEMS_PER_LOADING;
    this.somQuery.parameters.page = 1;
    this.total = { recipe: 0, product: 0, article: 0 };
  }

  public search(clearResult = true): void {

    if (clearResult) { this._clearResults(); }

    this.isRequestDone = false;
    this.prediggo.simplePageContent(this.somQuery).pipe(
      finalize(() => this.isRequestDone = true))
      .subscribe({
        next: response => {
          const results =  response?.blocks?.searches || [];
          this._handleResultsByPageType(results);
          this.total = this._calculateTotals(results, this.pageType);
          this._handleSearchEventOrRedirect(results);
          this.isResults = this.isResultsEmpty() === false
        },
        error: err => console.error(err),
    })
  }

  private _clearResults(): void {
    this.productsPage.results = [];
    this.recipesPage.results = [];
    this.articlesPage.results = [];
  }

  private _handleResultsByPageType(results): void {
    const adaptResults = (block, Adapter, additionalArgs = [], existingResults = []) => ({
      results: [
        ...(existingResults || []), // Concatène les anciens résultats s'ils existent pour la pagination
        ...(block?.slots?.map(slot => new Adapter(slot.item, ...additionalArgs).adapt()) || []).flat()
      ],
      facets: block?.facets,
      sortings: block?.sortings,
      totalResults: block?.stats?.totalResults,
    });
    
    if (this.pageType === PAGE_TYPE.ALL && results.length > 1) {
      const findBlock = (name) => results.find(b => b.blockName === name);
  
      this.productsPage = adaptResults(findBlock('Liste de produits'), PrediggoProductAdapter, [this.translate.currentLang]);
      this.recipesPage = adaptResults(findBlock('Liste de recettes'), PrediggoRecipeAdapter, [this.translate.currentLang]);
      this.articlesPage = adaptResults(findBlock('Liste des articles'), PrediggoArticleAdapter, [
        `${this.translate.instant('ROUTES.since-1853')}/${this.translate.instant('ROUTES.article')}`,
        `${this.translate.instant('ROUTES.magazine')}/${this.translate.instant('ROUTES.article')}`,
        `${this.translate.instant('ROUTES.faq')}`,
        `${this.translate.instant('ROUTES.menu-page')}/${this.translate.instant('ROUTES.article')}`,
      ]);
    } else {
      const resultsBlock = results[0];

      switch (this.pageType) {
        case PAGE_TYPE.PRODUCTS:
          this.productsPage = adaptResults(resultsBlock, PrediggoProductAdapter, [this.translate.currentLang], this.productsPage?.results);
          break;
  
        case PAGE_TYPE.RECIPES:
          this.recipesPage = adaptResults(resultsBlock, PrediggoRecipeAdapter, [this.translate.currentLang],  this.recipesPage?.results);
          break;
  
        case PAGE_TYPE.ARTICLES:
          this.articlesPage = adaptResults(resultsBlock, PrediggoArticleAdapter, [
            `${this.translate.instant('ROUTES.since-1853')}/${this.translate.instant('ROUTES.article')}`,
            `${this.translate.instant('ROUTES.magazine')}/${this.translate.instant('ROUTES.article')}`,
            `${this.translate.instant('ROUTES.faq')}`,
            `${this.translate.instant('ROUTES.menu-page')}/${this.translate.instant('ROUTES.article')}`,
          ], this.articlesPage?.results);
          break;
      }
    }
    this._filterResultsIfSearchIsGtin()
  }

  private _filterResultsIfSearchIsGtin(): void {
    if (isValidGtin(this.somQuery.parameters.query)) {
      const filteredProductsByGtin = this.productsPage.results.filter(p => p.gtin === this.somQuery.parameters.query)
      this.productsPage.results = filteredProductsByGtin ? filteredProductsByGtin : this.productsPage.results
    }
  }

  private _handleSearchEventOrRedirect(results): void {
    const {keywords, tagManager, total} = this;
    const redirectUrl = results[0].redirection ? results[0].redirection['redirectUrl'] : '';
    if (total.article > 0 || total.product > 0 || total.recipe > 0) {
        tagManager.setSearchEvent(total, keywords);
    } else {
        redirectUrl ? this._redirect(redirectUrl) : this.fetchPrediggoRecipesReco();
    }
  }


  private _redirect(url: string): void {
    if (url.includes('www.bonduelle.') || url.startsWith('/')) {
      const urlPath = url.includes('www.bonduelle') ? url.substring(24) : url;
      this.router.navigate([urlPath]);
    } else if (window) {
      this.windowReference = window.open(url, '_blank');
    }
  }

  private _calculateTotals(results, pageType: PAGE_TYPE): ResultTotal {
    const totals = this.total;
    const [ firstResult, secondResult, thirdResult ] = results;

    if (pageType === PAGE_TYPE.ALL && results.length > 1) {
      totals.recipe = firstResult.stats.totalResults;
      totals.product = this.productsPage.results.length;
      totals.article = thirdResult.stats.totalResults;
    } else if (pageType === PAGE_TYPE.PRODUCTS) {
      totals.product = this.productsPage.results.length;
    } else if (pageType === PAGE_TYPE.RECIPES) {
      totals.recipe = firstResult.stats.totalResults;
    } else if (pageType === PAGE_TYPE.ARTICLES) {
      totals.article = firstResult.stats.totalResults;
    }
    return totals
  }

  private isResultsEmpty(): boolean {
    return this.total.article === 0 && this.total.product === 0 && this.total.recipe === 0
  }

  public addOrRemoveFilter(filter: { name: string; value: string }): void {
    const newFilters: Record<string, string[]> = {};

    newFilters[filter.name] = this.filters[filter.name] || [];
    const foundIndex = newFilters[filter.name].indexOf(filter.value);

    foundIndex >= 0 
      ? newFilters[filter.name].splice(foundIndex, 1) 
      : newFilters[filter.name].push(filter.value);

    this.filters = { ...this.filters, ...newFilters };
  }

  public updateFilters(filters: Record<string, string[]>): void {
    this.filters = filters;
  }

  /**
   * Updates the current url
   * @memberof SearchMainService
   */
  public refactorUrl(): void {
    // Base url
    const translatedPath = this.getTranslatedPath(this.pageType);
    const search = this.translate.instant('ROUTES.search');
    const type = this.translate.instant(`ROUTES.${translatedPath}`);
    // if search have space replace it by dash
    const urlKeyword = charsMapConvert(this.keywords);
    // Keywords
    
    // Facets
    const entries = Object.entries(this.filters);
    const currentLang = this.translate.currentLang.split('_')[1];
    const prefix = this.translate.currentLang.split('_')[0] === 'be' ? `${currentLang}/` : ''

    let url = `${prefix}${search}/${urlKeyword}/${type}`;
    url = entries.reduce((acc, [key, values]) => {
      const translatedFacets = values.map(value => {
        const facet = encodeURI(`${key}/${value}`);
        return prediggoTranslation[currentLang][facet] || facet;
      });
      return acc + '/' + translatedFacets.join('/');
    }, url);
    
    // Update the current url
    this.location.replaceState(url);
  }

  private getTranslatedPath(pageType: PAGE_TYPE): string {
    const typeMapping = {
      articles: 'articles-search',
      products: 'products-search',
      recipes: 'recipes-home'
    };
    const typeKey = typeMapping[pageType] ? typeMapping[pageType] : 'all';
    return typeKey;
  }

  /**
   * Get a list of recipes recommended by Prediggo for page no results
   */
  public fetchPrediggoRecipesReco(): Observable<SomRecipePage[]> {
    let tRecipeListRecoSubject: AsyncSubject<SomRecipePage[]> = new AsyncSubject();

    if (isPlatformServer(this.platformId)) {
      tRecipeListRecoSubject = this.fetchPrediggoRecipesRecoSSR();
    } else if (isPlatformBrowser(this.platformId)) {
      if (!(this.currentRecipeList && this.currentRecipeList.length > 0) && this.transferState.hasKey(STATEKEY_RECIPE_LIST_RECO)) {
        const recipes = this.transferState.get(STATEKEY_RECIPE_LIST_RECO, this.currentRecipeList);
        this.recipeListSubject.next(recipes);
        tRecipeListRecoSubject.next(recipes);
        tRecipeListRecoSubject.complete();
      } else {
        tRecipeListRecoSubject = this.fetchPrediggoRecipesRecoSSR();
      }
    }
    return tRecipeListRecoSubject.asObservable();
  }

  private fetchPrediggoRecipesRecoSSR(): AsyncSubject<SomRecipePage[]> {
    const query: PrediggoSimplePageContent = PrediggoSimplePageContent.create();
    const tRecipeListRecoSubject: AsyncSubject<SomRecipePage[]> = new AsyncSubject();
    query.pageId = PrediggoPageEnum.page_404;

    this.prediggo.simplePageContent(query).subscribe(
      (res: PrediggoSimplePageContentResponse) => {
        let recipes: SomRecipePage[] = [];
        if (res?.blocks?.recommendations) {
          const recipeBlock: PrediggoRecoBlock[] = res.blocks.recommendations.filter(r => r.blockId === 0);
          if (recipeBlock?.[0]?.slots?.length) {
            // Here, we get the first item on the recommendations prediggo block
            recipes = recipeBlock[0].slots.map((slot: PrediggoSlot) => {
              const result = new PrediggoRecipeAdapter(slot.item, this.translate.currentLang).adapt();
              return result;
            });
          }
        }
        this.recipeListSubject.next(recipes);
        tRecipeListRecoSubject.next(recipes);
        this.transferState.set(STATEKEY_RECIPE_LIST_RECO, recipes);
        tRecipeListRecoSubject.complete();
      },
      (error: Error) => {
        tRecipeListRecoSubject.error(error);
      }
    );
    return tRecipeListRecoSubject;
  }
}
